import { Box, Tooltip } from "@mui/material";
import React, { useContext, useMemo, useState } from "react";
import StyledMainButton from "components/buttons/StyledMainButton";
import {
  useActionWithPath,
  useAgentLogs,
  useDeploymentInfoFull,
} from "utils/query";
import ResourcesContent, {
  Resources,
  ResourcesByKey,
} from "./ResourcesContent";
import { TabPanel } from "./TabPanel";
import UpdateDeploymentMemoryDialog from "components/dialogs/UpdateDeploymentMemoryDialog";
import { Message } from "components/SnackbarMessage";
import { MessageContext } from "utils/MessageProvider";
import { Refresh, Memory } from "@mui/icons-material";
import { isWorkerHostingEnvironment } from "utils/saastypes";

export default function ResourcesTab({
  selectedTabIndex,
  index,
  clusterDetail,
}: {
  selectedTabIndex: number;
  index: number;
  clusterDetail?: { name?: string; workerHosting?: boolean };
}) {
  const { setMessage } = useContext(MessageContext);
  const { refetch: refetchLogs } = useAgentLogs(clusterDetail?.name ?? "");

  const showError = (message: string) =>
    setMessage(new Message(message, "error"));
  const showSuccess = (message: string) =>
    setMessage(new Message(message, "success"));

  const [memoryDialogOpen, setMemoryDialogOpen] = useState(false);
  const [memoryDialogResource, setMemoryDialogResource] = useState<
    "CONDUCTOR" | "WORKERS"
  >("CONDUCTOR");

  const {
    data: deploymentInfo,
    isFetching,
    error,
    refetch,
  } = useDeploymentInfoFull(clusterDetail?.name ?? "");

  // Trigger agent workflow to refresh resources, then refetch cached data
  const triggerGetDeploymentsInfo = useActionWithPath({}).mutateAsync;
  async function refreshResources() {
    try {
      // @ts-ignore
      const res = await triggerGetDeploymentsInfo({
        method: "post",
        path: `/agent/command`,
        body: JSON.stringify({
          clusterName: clusterDetail?.name,
          command: "GET_DEPLOYMENTS_INFO",
          parameters: {},
        }),
      });
      if (res && res.status === "SUCCESS") {
        setTimeout(() => refetch(), 1200);
      }
    } catch (e) {
      // ignore; UI will reflect via query error if needed
    }
  }

  const resourcesByKey: ResourcesByKey = {};
  if (deploymentInfo) {
    Object.keys(deploymentInfo as Record<string, any>).forEach((k) => {
      const res = (deploymentInfo as Record<string, any>)[k]?.resources as
        | Resources
        | undefined;
      if (res) {
        resourcesByKey[k] = res;
      }
    });
  }

  const keys = Object.keys(resourcesByKey);
  const noData = !keys.length;

  const handleOpenUpdateMemory = (resourceKey: "CONDUCTOR" | "WORKERS") => {
    setMemoryDialogResource(resourceKey);
    setMemoryDialogOpen(true);
  };

  const renderActionsForKey = useMemo(() => {
    return (k: string) => {
      const lower = k.toLowerCase();
      const isConductor = lower.includes("conductor");
      const isWorker = lower.includes("worker");
      if (!isConductor && !isWorker) return null;
      return (
        <Tooltip title="Update memory">
          <StyledMainButton
            size="small"
            startIcon={<Memory fontSize="small" />}
            sx={{ height: 30, minHeight: 30, padding: "0 10px", fontSize: 12 }}
            onClick={() =>
              handleOpenUpdateMemory(isConductor ? "CONDUCTOR" : "WORKERS")
            }
          >
            Update Memory
          </StyledMainButton>
        </Tooltip>
      );
    };
  }, []);

  // Priority: conductor -> workers -> agent -> others (alphabetical)
  function getPriority(key: string): number {
    const s = key.toLowerCase();
    if (s.includes("conductor")) return 0;
    if (s.includes("worker")) return 1;
    if (s.includes("agent")) return 2;
    return 3;
  }
  // Worker hosting (CLOUD-1193) deploys no Conductor server, so its card would be empty —
  // only the workers and agent deployments apply. Sizing is still inspected/adjusted here.
  const isWorkerHosting = isWorkerHostingEnvironment(clusterDetail);
  const orderedKeys = [...keys]
    .filter(
      (key) => !isWorkerHosting || !key.toLowerCase().includes("conductor")
    )
    .sort((a, b) => {
      const pa = getPriority(a);
      const pb = getPriority(b);
      if (pa !== pb) return pa - pb;
      return a.localeCompare(b);
    });

  function toNumber(val: string | number | null | undefined): number | null {
    if (val === null || val === undefined || val === "") return null;
    const n = Number(val);
    return isNaN(n) ? null : n;
  }

  // Convert memory strings/numbers to human MiB/GiB
  function formatMemory(raw: string | number | null | undefined): string {
    if (raw === null || raw === undefined || raw === "") return "-";
    const s = String(raw).trim();
    // Handle Kubernetes style like "512Mi" or "2Gi"
    if (/^\d+(\.\d+)?Mi$/i.test(s)) {
      const mi = parseFloat(s.replace(/Mi/i, ""));
      return mi >= 1024 ? `${(mi / 1024).toFixed(1)} GiB` : `${mi} MiB`;
    }
    if (/^\d+(\.\d+)?Gi$/i.test(s)) {
      const gi = parseFloat(s.replace(/Gi/i, ""));
      return `${gi} GiB`;
    }
    // Assume bytes if plain number
    const asNum = toNumber(s);
    if (asNum !== null) {
      const mi = asNum / (1024 * 1024);
      return mi >= 1024
        ? `${(mi / 1024).toFixed(1)} GiB`
        : `${Math.round(mi)} MiB`;
    }
    // Fallback: render as-is
    return s;
  }

  function formatDeploymentKey(key: string): string {
    return key.replace(/[-_]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  }

  return (
    <TabPanel value={selectedTabIndex} index={index}>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          borderBottom: "1px solid lightgrey",
        }}
      >
        <Box>
          <StyledMainButton
            startIcon={<Refresh />}
            id="refresh-resources-btn"
            style={{ margin: 16 }}
            disabled={isFetching}
            onClick={refreshResources}
          >
            Refresh Resources
          </StyledMainButton>
        </Box>
        <ResourcesContent
          isFetching={isFetching}
          error={error}
          noData={noData}
          orderedKeys={orderedKeys}
          resourcesByKey={resourcesByKey}
          formatDeploymentKey={formatDeploymentKey}
          formatMemory={formatMemory}
          renderActionsForKey={renderActionsForKey}
        />
      </Box>
      <UpdateDeploymentMemoryDialog
        open={memoryDialogOpen}
        closeDialog={() => setMemoryDialogOpen(false)}
        clusterName={clusterDetail?.name}
        showError={showError}
        showSuccess={showSuccess}
        refetchLogs={refetchLogs}
        initialResource={memoryDialogResource}
      />
    </TabPanel>
  );
}
